import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { parseISO, format } from 'date-fns';
import {
  TimeEntry,
  timeEntriesAPI,
  projectsAPI,
  clientsAPI,
  usersAPI,
  tagsAPI,
  type Tag,
} from '../../services/api';
import ClockifyImport from './ClockifyImport';
import SmartTimeEntryForm from '../SmartTimeEntryForm';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useAuth } from '../../context/AuthContext';
import {
  PlusIcon,
  ClockIcon,
  ArrowLeftIcon,
  SearchIcon,
  DownloadIcon,
  CheckIcon,
  EditIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserIcon,
  FolderIcon,
  CalendarIcon,
  FilterIcon,
} from '../icons';

// Premium line icons (1.5px stroke weight)
const Icons = {
  plus: <PlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  clock: <ClockIcon className="" width={20} height={20} strokeWidth={1.5} />,
  arrowLeft: (
    <ArrowLeftIcon className="" width={18} height={18} strokeWidth={1.5} />
  ),
  search: <SearchIcon className="" width={18} height={18} strokeWidth={1.5} />,
  download: (
    <DownloadIcon className="" width={18} height={18} strokeWidth={1.5} />
  ),
  check: <CheckIcon className="" width={18} height={18} strokeWidth={1.5} />,
  edit: <EditIcon className="" width={16} height={16} strokeWidth={1.5} />,
  trash: <TrashIcon className="" width={16} height={16} strokeWidth={1.5} />,
  checkCircle: (
    <CheckCircleIcon className="" width={16} height={16} strokeWidth={1.5} />
  ),
  xCircle: (
    <XCircleIcon className="" width={16} height={16} strokeWidth={1.5} />
  ),
  user: <UserIcon className="" width={16} height={16} strokeWidth={1.5} />,
  folder: <FolderIcon className="" width={16} height={16} strokeWidth={1.5} />,
  calendar: (
    <CalendarIcon className="" width={16} height={16} strokeWidth={1.5} />
  ),
  filter: <FilterIcon className="" width={18} height={18} strokeWidth={1.5} />,
};

interface TimeEntryWithDetails {
  id?: number;
  entry_date?: string;
  start_time?: string;
  end_time?: string;
  hours_worked?: number;
  description: string;
  is_sred_eligible?: boolean;
  is_cdae_eligible?: boolean;
  invoiced?: boolean;
  approval_status?: string;
  user: {
    id: number;
    full_name: string;
    email: string;
  };
  project: {
    id: number;
    name: string;
  };
  sred_activity_category: {
    id: number;
    name: string;
    code: string;
  };
  tags?: Tag[];
  tag_ids?: number[];
}

// Component for creating a new time entry (manager view)
const NewTimeEntryForm: React.FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleSuccess = () => {
    onSuccess();
    navigate('/manager/time-entries');
  };

  const handleCancel = () => {
    navigate('/manager/time-entries');
  };

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      <div className="mb-6">
        <Link
          to="/manager/time-entries"
          className={`inline-flex items-center gap-2 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
        >
          {Icons.arrowLeft}
          {t('common.back')}
        </Link>
      </div>
      <SmartTimeEntryForm onSuccess={handleSuccess} onCancel={handleCancel} />
    </div>
  );
};

// Component for editing an existing time entry (manager view)
const EditTimeEntryForm: React.FC<{ onSuccess: () => void }> = ({
  onSuccess,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [entry, setEntry] = useState<TimeEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEntry = async () => {
      if (!id) return;
      try {
        const response = await timeEntriesAPI.getById(parseInt(id));
        setEntry(response.data.time_entry || response.data);
        setLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load time entry');
        setLoading(false);
      }
    };
    fetchEntry();
  }, [id]);

  const handleSuccess = () => {
    onSuccess();
    navigate('/manager/time-entries');
  };

  const handleCancel = () => {
    navigate('/manager/time-entries');
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="flex justify-center items-center p-8">
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-violet-400' : 'border-violet-600'}`}
          ></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
          {error}
          <Link to="/manager/time-entries" className="ml-4 underline">
            {t('common.back')}
          </Link>
        </div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="p-4 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-xl">
          {t('timeEntry.notFound')}
          <Link to="/manager/time-entries" className="ml-4 underline">
            {t('common.back')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      <div className="mb-6">
        <Link
          to="/manager/time-entries"
          className={`inline-flex items-center gap-2 transition-colors ${isDark ? 'text-slate-400 hover:text-white' : 'text-slate-600 hover:text-slate-900'}`}
        >
          {Icons.arrowLeft}
          {t('common.back')}
        </Link>
      </div>
      <SmartTimeEntryForm
        timeEntry={entry}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </div>
  );
};

const TimeEntryListView: React.FC<{ onRefresh?: () => void }> = ({
  onRefresh,
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [timeEntries, setTimeEntries] = useState<TimeEntryWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [projects, setProjects] = useState<any[]>([]);
  const [clients, setClients] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [orgTags, setOrgTags] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<number[]>([]);

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const tableHeaderClass = isDark
    ? 'bg-slate-900/80 text-slate-400'
    : 'bg-slate-50 text-slate-500';

  const tableRowClass = isDark
    ? 'border-slate-700/50 hover:bg-slate-700/30'
    : 'border-slate-100 hover:bg-slate-50';

  const formatEntryTime = (value?: string) => {
    if (!value) return '';

    const hhmmMatch = value.match(/^(\d{2}:\d{2})/);
    if (hhmmMatch) return hhmmMatch[1];

    try {
      return format(parseISO(value), 'HH:mm');
    } catch {
      return value;
    }
  };

  useEffect(() => {
    fetchData();
  }, [statusFilter, projectFilter, clientFilter, userFilter, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (projectFilter) params.project_id = projectFilter;
      if (clientFilter) params.client_id = clientFilter;
      if (userFilter) params.user_id = userFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const [
        timeEntriesResponse,
        projectsResponse,
        clientsResponse,
        tagsResponse,
      ] = await Promise.all([
        timeEntriesAPI.getAll(params),
        projectsAPI.getAll(),
        clientsAPI.getAll(),
        tagsAPI.getAll(),
      ]);

      setTimeEntries(timeEntriesResponse.data.time_entries || []);
      setProjects(projectsResponse.data.projects || []);
      setClients(clientsResponse.data.clients || []);
      setOrgTags(tagsResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('timeEntryMgmt.deleteTitle'),
      message: t('timeEntryMgmt.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await timeEntriesAPI.delete(id);
      setTimeEntries(timeEntries.filter((t) => t.id !== id));
    } catch (error) {
      console.error('Failed to delete time entry:', error);
      showToast(t('timeEntryMgmt.deleteFailed'), 'error');
    }
  };

  const handleSelectEntry = (entryId: number, checked: boolean) => {
    setSelectedEntries(
      checked
        ? [...selectedEntries, entryId]
        : selectedEntries.filter((id) => id !== entryId),
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedEntries(checked ? filteredTimeEntries.map((e) => e.id!) : []);
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.length === 0) {
      showToast(t('timeEntryMgmt.selectEntriesToApprove'), 'warning');
      return;
    }

    try {
      await timeEntriesAPI.bulkApprove(selectedEntries);
      fetchData();
      setSelectedEntries([]);
      showToast(`${selectedEntries.length} entries approved`, 'success');
    } catch (error) {
      console.error('Failed to bulk approve:', error);
      showToast(t('timeEntryMgmt.bulkApproveFailed'), 'error');
    }
  };

  const handleBulkReject = async () => {
    if (selectedEntries.length === 0) {
      showToast('Select entries to reject', 'warning');
      return;
    }
    const reason = prompt('Rejection reason (optional):');
    try {
      await timeEntriesAPI.bulkReject(selectedEntries, reason || undefined);
      fetchData();
      setSelectedEntries([]);
      showToast(`${selectedEntries.length} entries rejected`, 'success');
    } catch (error) {
      console.error('Failed to bulk reject:', error);
      showToast('Failed to reject entries', 'error');
    }
  };

  const handleBulkDelete = async () => {
    const confirmed = await confirm({
      title: 'Delete entries?',
      message: `Permanently delete ${selectedEntries.length} time entries?`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await timeEntriesAPI.bulkDelete(selectedEntries);
      fetchData();
      setSelectedEntries([]);
      showToast(`${selectedEntries.length} entries deleted`, 'success');
    } catch (error) {
      console.error('Failed to bulk delete:', error);
      showToast('Failed to delete entries', 'error');
    }
  };

  const handleBulkMarkInvoiced = async () => {
    if (selectedEntries.length === 0) return;
    try {
      await timeEntriesAPI.bulkMarkInvoiced(selectedEntries, true);
      fetchData();
      setSelectedEntries([]);
      showToast(
        `${selectedEntries.length} entries marked as invoiced`,
        'success',
      );
    } catch (error) {
      console.error('Failed to mark entries as invoiced:', error);
      showToast('Failed to mark entries as invoiced', 'error');
    }
  };

  const handleToggleInvoiced = async (
    entryId: number,
    currentStatus: boolean,
  ) => {
    if (currentStatus && user?.role !== 'admin') {
      showToast('Only admins can uninvoice entries', 'error');
      return;
    }
    try {
      await timeEntriesAPI.bulkMarkInvoiced([entryId], !currentStatus);
      // Update the local state
      setTimeEntries(
        timeEntries.map((entry) =>
          entry.id === entryId ? { ...entry, invoiced: !currentStatus } : entry,
        ),
      );
      showToast(
        !currentStatus
          ? t('timeEntryMgmt.invoiced')
          : t('timeEntryMgmt.notInvoiced'),
        'success',
      );
    } catch (error) {
      console.error('Failed to update invoice status:', error);
      showToast('Failed to update invoice status', 'error');
    }
  };

  const handleImportComplete = () => {
    fetchData();
  };

  const filteredTimeEntries = timeEntries.filter((entry) => {
    const matchesSearch =
      entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.user?.full_name &&
        entry.user.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'pending':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'approved':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'rejected':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (status) {
        case 'pending':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'approved':
          return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
        case 'rejected':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  const totalHours = filteredTimeEntries.reduce(
    (sum, entry) =>
      sum + (parseFloat(entry.hours_worked?.toString() || '0') || 0),
    0,
  );
  const billableHours = filteredTimeEntries
    .filter((e) => e.is_sred_eligible || e.is_cdae_eligible)
    .reduce(
      (sum, entry) =>
        sum + (parseFloat(entry.hours_worked?.toString() || '0') || 0),
      0,
    );

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div className="animate-pulse">
          <div
            className={`h-10 ${isDark ? 'bg-slate-700' : 'bg-slate-200'} rounded-xl w-1/4 mb-6`}
          ></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className={`h-16 ${isDark ? 'bg-slate-900' : 'bg-slate-200'} rounded-xl`}
              ></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      {/* Header */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div
                className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
              >
                <span
                  className={isDark ? 'text-violet-400' : 'text-violet-600'}
                >
                  {Icons.clock}
                </span>
              </div>
              <h1
                className={`text-xl sm:text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('timeEntryMgmt.title')}
              </h1>
            </div>
            <p
              className={`text-xs sm:text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
            >
              {t('timeEntryMgmt.total')}: {(totalHours || 0).toFixed(1)}h |{' '}
              {t('timeEntryMgmt.billable')}: {(billableHours || 0).toFixed(1)}h
              | {t('timeEntryMgmt.entries')}: {filteredTimeEntries.length}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowImportModal(true)}
              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-semibold rounded-xl transition-all duration-200 ${
                isDark
                  ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30'
                  : 'bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200'
              }`}
            >
              {Icons.download}
              <span className="hidden sm:inline">
                {t('timeEntryMgmt.importClockify')}
              </span>
              <span className="sm:hidden">Import</span>
            </button>
            <button
              onClick={handleBulkApprove}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-emerald-500/25 transition-all duration-200"
            >
              {Icons.check}
              <span className="hidden sm:inline">
                {t('timeEntryMgmt.bulkApprove')}
              </span>
              <span className="sm:hidden">{t('approval.approve')}</span>
            </button>
            <Link
              to="/manager/time-entries/new"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-3 sm:px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
            >
              {Icons.plus}
              <span className="hidden sm:inline">
                {t('timeEntryMgmt.newEntry')}
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className={`${cardClass} rounded-2xl p-4 mb-6`}>
        {/* Search always visible */}
        <div className="relative mb-4 md:mb-0 md:hidden">
          <span
            className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {Icons.search}
          </span>
          <input
            type="text"
            placeholder={t('common.search')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${inputClass} pl-11`}
          />
        </div>

        {/* Mobile filter toggle */}
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`md:hidden w-full flex items-center justify-between py-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            {Icons.filter}
            {t('common.filters')}
            {(statusFilter ||
              projectFilter ||
              clientFilter ||
              dateFrom ||
              dateTo) && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-violet-500 text-white">
                {
                  [
                    statusFilter,
                    projectFilter,
                    clientFilter,
                    dateFrom,
                    dateTo,
                  ].filter(Boolean).length
                }
              </span>
            )}
          </span>
          <svg
            className={`w-5 h-5 transition-transform ${filtersOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>

        {/* Filters grid - collapsible on mobile */}
        <div className={`${filtersOpen ? 'block' : 'hidden'} md:block`}>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4 md:mt-0">
            {/* Search - hidden on mobile (shown above) */}
            <div className="relative hidden md:block">
              <span
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
              >
                {Icons.search}
              </span>
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${inputClass} pl-11`}
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('timeEntryMgmt.allStatus')}</option>
              <option value="pending">
                {t('timeEntryMgmt.status.pending')}
              </option>
              <option value="approved">
                {t('timeEntryMgmt.status.approved')}
              </option>
              <option value="rejected">
                {t('timeEntryMgmt.status.rejected')}
              </option>
            </select>
            <select
              value={projectFilter}
              onChange={(e) => setProjectFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('timeEntryMgmt.allProjects')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <select
              value={clientFilter}
              onChange={(e) => setClientFilter(e.target.value)}
              className={selectClass}
            >
              <option value="">{t('reports.allClients')}</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              placeholder={t('timeEntryMgmt.fromDate')}
              className={inputClass}
            />
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              placeholder={t('timeEntryMgmt.toDate')}
              className={inputClass}
            />
          </div>
        </div>
      </div>

      {/* Time Entries Table */}
      <div className={`${cardClass} rounded-2xl overflow-hidden`}>
        {filteredTimeEntries.length === 0 ? (
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
            >
              <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
                {Icons.clock}
              </span>
            </div>
            <p className="font-medium">{t('timeEntryMgmt.noEntries')}</p>
          </div>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div
              className={`md:hidden divide-y ${isDark ? 'divide-slate-700/50' : 'divide-slate-100'}`}
            >
              {filteredTimeEntries.map((entry) => (
                <div
                  key={entry.id}
                  className={`p-4 ${entry.approval_status === 'pending' ? (isDark ? 'bg-amber-500/5' : 'bg-amber-50/50') : ''}`}
                >
                  {/* Date, Hours & Status */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id={`entry-mobile-${entry.id}`}
                        disabled={entry.approval_status !== 'pending'}
                        className="rounded border-slate-300"
                      />
                      <span
                        className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {entry.entry_date
                          ? format(parseISO(entry.entry_date), 'M/d/yyyy')
                          : '-'}
                      </span>
                      <span
                        className={`text-sm font-bold ${isDark ? 'text-violet-400' : 'text-violet-600'}`}
                      >
                        {(
                          parseFloat(entry.hours_worked?.toString() || '0') || 0
                        ).toFixed(1)}
                        h
                      </span>
                    </div>
                    <span
                      className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${getStatusColor(entry.approval_status || 'pending')}`}
                    >
                      {entry.approval_status || 'pending'}
                    </span>
                  </div>

                  {/* User */}
                  <div
                    className={`text-sm font-medium mb-1 ${isDark ? 'text-slate-200' : 'text-slate-800'}`}
                  >
                    {entry.user?.full_name || t('timeEntryMgmt.unknownUser')}
                  </div>

                  {/* Project */}
                  <div
                    className={`text-xs mb-1 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
                  >
                    {entry.project?.name || t('timeEntryMgmt.noProject')}
                  </div>

                  {/* Description */}
                  <p
                    className={`text-xs truncate mb-2 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                  >
                    {entry.description}
                  </p>

                  {/* Badges & Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {entry.is_sred_eligible && (
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded-lg ${isDark ? 'bg-violet-500/20 text-violet-400' : 'bg-violet-50 text-violet-700'}`}
                        >
                          SR&ED
                        </span>
                      )}
                      {entry.is_cdae_eligible && (
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs rounded-lg ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-50 text-cyan-700'}`}
                        >
                          CDAE
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <Link
                        to={`/manager/time-entries/${entry.id}/edit`}
                        className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                      >
                        {Icons.edit}
                      </Link>
                      {entry.approval_status === 'pending' && (
                        <>
                          <Link
                            to={`/manager/approvals/${entry.id}/approve`}
                            className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                          >
                            {Icons.checkCircle}
                          </Link>
                          <Link
                            to={`/manager/approvals/${entry.id}/reject`}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                          >
                            {Icons.xCircle}
                          </Link>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(entry.id!)}
                        className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      >
                        {Icons.trash}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table Layout */}
            <div
              className={`hidden md:block ${cardClass} rounded-2xl overflow-hidden`}
            >
              {selectedEntries.length > 0 && (
                <div
                  className={`p-4 flex items-center justify-between gap-4 flex-wrap ${isDark ? 'bg-violet-900/30 border-b border-violet-700/50' : 'bg-violet-50 border-b border-violet-200'}`}
                >
                  <span
                    className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}
                  >
                    {selectedEntries.length}{' '}
                    {selectedEntries.length === 1
                      ? 'entry selected'
                      : 'entries selected'}
                  </span>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleBulkApprove}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {t('timeEntryMgmt.bulkApprove')}
                    </button>
                    <button
                      onClick={handleBulkReject}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleBulkMarkInvoiced}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      Mark as invoiced
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </div>
              )}
              <div
                ref={tableContainerRef}
                className={`overflow-auto max-h-[calc(100vh-450px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
              >
                <table className="min-w-full relative">
                  <thead className={`sticky top-0 z-10 ${tableHeaderClass}`}>
                    <tr>
                      <th className="px-6 py-4 text-left">
                        <input
                          type="checkbox"
                          checked={
                            selectedEntries.length > 0 &&
                            selectedEntries.length ===
                              filteredTimeEntries.length
                          }
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="rounded border-slate-300"
                        />
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.date')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.user')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.project')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.description')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.hours')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.status')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.columns.type')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('common.tags') || 'Tags'}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('timeEntryMgmt.invoice') || 'Invoice'}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                        {t('common.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody
                    className={
                      isDark
                        ? 'divide-y divide-slate-700/50'
                        : 'divide-y divide-slate-100'
                    }
                  >
                    {filteredTimeEntries.map((entry) => (
                      <tr
                        key={entry.id}
                        className={`${tableRowClass} ${entry.approval_status === 'pending' ? (isDark ? 'bg-amber-500/5' : 'bg-amber-50/50') : ''}`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedEntries.includes(entry.id!)}
                            onChange={(e) =>
                              handleSelectEntry(entry.id!, e.target.checked)
                            }
                            className="rounded border-slate-300"
                          />
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={
                                isDark ? 'text-slate-500' : 'text-slate-400'
                              }
                            >
                              {Icons.calendar}
                            </span>
                            {entry.entry_date
                              ? format(parseISO(entry.entry_date), 'M/d/yyyy')
                              : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {entry.user?.full_name ||
                              t('timeEntryMgmt.unknownUser')}
                          </div>
                          <div
                            className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                          >
                            {entry.user?.email}
                          </div>
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              className={
                                isDark ? 'text-slate-500' : 'text-slate-400'
                              }
                            >
                              {Icons.folder}
                            </span>
                            {entry.project?.name ||
                              t('timeEntryMgmt.noProject')}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div
                            className={`text-sm max-w-xs truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {entry.description}
                          </div>
                          {entry.sred_activity_category && (
                            <div
                              className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {entry.sred_activity_category.name}
                            </div>
                          )}
                        </td>
                        <td
                          className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                        >
                          {(
                            parseFloat(entry.hours_worked?.toString() || '0') ||
                            0
                          ).toFixed(1)}
                          h
                          {entry.start_time && entry.end_time && (
                            <div
                              className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}
                            >
                              {formatEntryTime(entry.start_time)} -{' '}
                              {formatEntryTime(entry.end_time)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${getStatusColor(entry.approval_status || 'pending')}`}
                          >
                            {entry.approval_status || 'pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col gap-1">
                            {entry.is_sred_eligible && (
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs rounded-lg ${isDark ? 'bg-violet-500/20 text-violet-400 border border-violet-500/30' : 'bg-violet-50 text-violet-700 border border-violet-200'}`}
                              >
                                {t('compliance.sred')}
                              </span>
                            )}
                            {entry.is_cdae_eligible && (
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs rounded-lg ${isDark ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-cyan-50 text-cyan-700 border border-cyan-200'}`}
                              >
                                {t('compliance.cdae')}
                              </span>
                            )}
                            {!entry.is_sred_eligible &&
                              !entry.is_cdae_eligible && (
                                <span
                                  className={`inline-flex px-2 py-0.5 text-xs rounded-lg ${isDark ? 'bg-slate-500/20 text-slate-400 border border-slate-500/30' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}
                                >
                                  {t('timeEntryMgmt.regular')}
                                </span>
                              )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex flex-wrap gap-1">
                            {entry.tags &&
                              entry.tags.slice(0, 2).map((tag: any) => (
                                <span
                                  key={tag.id}
                                  style={{
                                    backgroundColor: `#${tag.color}26`,
                                    color: `#${tag.color}`,
                                    borderColor: `#${tag.color}4d`,
                                  }}
                                  className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full border"
                                >
                                  {tag.name}
                                </span>
                              ))}
                            {entry.tags && entry.tags.length > 2 && (
                              <span
                                className={`text-xs font-medium px-2 py-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                              >
                                +{entry.tags.length - 2}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={entry.invoiced || false}
                              onChange={(e) =>
                                handleToggleInvoiced(
                                  entry.id!,
                                  entry.invoiced || false,
                                )
                              }
                              className={`rounded border-2 transition-all ${
                                isDark
                                  ? 'border-slate-600 bg-slate-800 checked:bg-emerald-600 checked:border-emerald-600 cursor-pointer'
                                  : 'border-slate-300 bg-white checked:bg-emerald-600 checked:border-emerald-600 cursor-pointer'
                              }`}
                            />
                            <span
                              className={`text-xs font-medium ${
                                entry.invoiced
                                  ? isDark
                                    ? 'text-emerald-400'
                                    : 'text-emerald-700'
                                  : isDark
                                    ? 'text-slate-500'
                                    : 'text-slate-500'
                              }`}
                            >
                              {entry.invoiced
                                ? t('timeEntryMgmt.invoiced')
                                : t('timeEntryMgmt.notInvoiced')}
                            </span>
                          </label>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <Link
                              to={`/manager/time-entries/${entry.id}/edit`}
                              className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                              title={t('common.edit')}
                            >
                              {Icons.edit}
                            </Link>
                            {entry.approval_status === 'pending' && (
                              <>
                                <Link
                                  to={`/manager/approvals/${entry.id}/approve`}
                                  className="p-2 rounded-lg text-emerald-500 hover:bg-emerald-500/10 transition-all"
                                  title={t('approval.approve')}
                                >
                                  {Icons.checkCircle}
                                </Link>
                                <Link
                                  to={`/manager/approvals/${entry.id}/reject`}
                                  className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                                  title={t('approval.reject')}
                                >
                                  {Icons.xCircle}
                                </Link>
                              </>
                            )}
                            <button
                              onClick={() => handleDelete(entry.id!)}
                              className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                              title={t('common.delete')}
                            >
                              {Icons.trash}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className={`${cardClass} rounded-2xl p-5`}>
          <div
            className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            {t('timeEntryMgmt.totalHours')}
          </div>
          <div
            className={`text-3xl font-bold mt-1 ${isDark ? 'text-white' : 'text-slate-900'}`}
          >
            {(totalHours || 0).toFixed(1)}h
          </div>
        </div>
        <div className={`${cardClass} rounded-2xl p-5`}>
          <div
            className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            {t('timeEntryMgmt.billableHours')}
          </div>
          <div className="text-3xl font-bold mt-1 text-emerald-500">
            {(billableHours || 0).toFixed(1)}h
          </div>
        </div>
        <div className={`${cardClass} rounded-2xl p-5`}>
          <div
            className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            {t('timeEntryMgmt.pendingApproval')}
          </div>
          <div className="text-3xl font-bold mt-1 text-amber-500">
            {
              filteredTimeEntries.filter((e) => e.approval_status === 'pending')
                .length
            }
          </div>
        </div>
        <div className={`${cardClass} rounded-2xl p-5`}>
          <div
            className={`text-sm font-medium ${isDark ? 'text-slate-400' : 'text-slate-600'}`}
          >
            {t('timeEntryMgmt.approved')}
          </div>
          <div className="text-3xl font-bold mt-1 text-emerald-500">
            {
              filteredTimeEntries.filter(
                (e) => e.approval_status === 'approved',
              ).length
            }
          </div>
        </div>
      </div>

      {/* Clockify Import Modal */}
      {showImportModal && (
        <ClockifyImport
          onClose={() => setShowImportModal(false)}
          onImportComplete={handleImportComplete}
        />
      )}
    </div>
  );
};

const TimeEntryManagement: React.FC = () => {
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  return (
    <Routes>
      <Route
        index
        element={
          <TimeEntryListView key={refreshTrigger} onRefresh={handleRefresh} />
        }
      />
      <Route
        path="new"
        element={<NewTimeEntryForm onSuccess={handleRefresh} />}
      />
      <Route
        path=":id/edit"
        element={<EditTimeEntryForm onSuccess={handleRefresh} />}
      />
    </Routes>
  );
};

export default TimeEntryManagement;
